import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Text,
  Flex,
  SimpleGrid,
  Spinner,
  HStack,
  Breadcrumb,
  Input,
} from "@chakra-ui/react";
import { MdCategory, MdChevronRight, MdDelete, MdEdit, MdLocalOffer } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { TagCombobox } from "components/utils/TagCombobox";
import { useDialogForm } from "utils/useDialogForm";
import {
  useDashboardActividadesMeta,
  useDashboardActividades,
  useDeleteDashboardActividad,
  usePatchDashboardActividad,
} from "features/dashboard/actividades/hooks";
import {
  useDashboardCategoria,
  useDeleteDashboardCategoria,
  usePatchDashboardCategoria,
} from "features/dashboard/categorias/hooks";
import { useCreateDashboardTag, useDashboardTags } from "features/dashboard/tags/hooks";
import { sortTagsForDisplay } from "features/dashboard/tags/sort";
import {
  normalizeActividadForCard,
  compareActividadesForDisplay,
} from "../Actividades/actividadesList.helpers";
import CardActs from "../Actividades/components/CardActs";

const CategoriaDetail = () => {
  const { id: categoriaId } = useParams();
  const navigate = useNavigate();
  const metaQ = useDashboardActividadesMeta();
  const actsQ = useDashboardActividades();
  const categoriaQ = useDashboardCategoria(categoriaId);
  const tagsQ = useDashboardTags();
  const createTag = useCreateDashboardTag();
  const patchCategoria = usePatchDashboardCategoria();
  const deleteCategoria = useDeleteDashboardCategoria();
  const deleteAct = useDeleteDashboardActividad();
  const patchAct = usePatchDashboardActividad();
  const editDialog = useDialogForm({ nombre: "", tagIds: [] });

  const categorias = metaQ.data?.categorias ?? [];
  const categoriaFromMeta = categorias.find((c) => c.id === categoriaId);
  const categoria = categoriaQ.data ?? categoriaFromMeta;
  const categoriaTags = Array.isArray(categoria?.tags) ? categoria.tags : [];
  const tags = useMemo(
    () => sortTagsForDisplay(Array.isArray(tagsQ.data) ? tagsQ.data : []),
    [tagsQ.data]
  );

  const actividadesFiltradas = useMemo(() => {
    const rows = Array.isArray(actsQ.data) ? actsQ.data : [];
    return rows
      .filter((a) => Array.isArray(a.categoriaIds) && a.categoriaIds.includes(categoriaId))
      .sort(compareActividadesForDisplay);
  }, [actsQ.data, categoriaId]);

  const cards = useMemo(
    () => actividadesFiltradas.map(normalizeActividadForCard),
    [actividadesFiltradas]
  );

  const onEditOpen = () => {
    editDialog.openWith({
      nombre: categoria?.nombre ?? "",
      tagIds: categoria?.tagIds ?? categoriaTags.map((tag) => tag.id),
    });
  };

  const onEditSave = async () => {
    const nombre = editDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await patchCategoria.mutateAsync({
        categoriaId,
        patch: { nombre, tagIds: editDialog.form.tagIds },
      });
      editDialog.close();
    } catch (_e) {}
  };

  const onDelete = (id) => deleteAct.mutate(id);
  const onTogglePublish = (id, published) =>
    patchAct.mutate({ actividadId: id, patch: { published: !published } });
  const onDeleteCategoria = async () => {
    if (!window.confirm("Eliminar esta categoria?")) return;
    try {
      await deleteCategoria.mutateAsync(categoriaId);
      navigate("/dashboard/club/categorias");
    } catch (_e) {}
  };

  const isLoading = metaQ.isLoading || actsQ.isLoading || categoriaQ.isLoading;
  const crumbTitle = categoria?.nombre ?? "Categoría";

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumb.Root pb="2">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link asChild>
              <Link to="/dashboard/club/categorias">
                <HStack gap="1">
                  <MdCategory />
                  <Text>Categorías</Text>
                </HStack>
              </Link>
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator>
            <MdChevronRight />
          </Breadcrumb.Separator>
          <Breadcrumb.Item>
            <Breadcrumb.CurrentLink>{crumbTitle}</Breadcrumb.CurrentLink>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb.Root>

      {/* Header */}
      <Flex
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        mb="6"
        gap="4"
        direction={{ base: "column", sm: "row" }}
      >
        <Flex align="center" gap="3">
          <Box
            w="48px"
            h="48px"
            borderRadius="14px"
            bg="purple.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <MdCategory size="24px" color="white" />
          </Box>
          <Box>
            <Text
              fontFamily="display"
              fontWeight="700"
              fontSize={{ base: "2xl", md: "3xl" }}
              color="dashboard.fg.default"
              lineHeight="1.1"
            >
              {crumbTitle}
            </Text>
            <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
              {cards.length} {cards.length === 1 ? "actividad" : "actividades"}
            </Text>
          </Box>
        </Flex>

        <HStack gap="2" alignSelf={{ base: "stretch", sm: "auto" }}>
          <Button
            variant="solid"
            color="red"
            onClick={onEditOpen}
            radius="xl"
            flex={{ base: 1, sm: "initial" }}
          >
            <MdEdit />
            Editar
          </Button>
          <Button
            variant="outline"
            color="dark"
            onClick={onDeleteCategoria}
            radius="xl"
            disabled={deleteCategoria.isPending}
            flex={{ base: 1, sm: "initial" }}
          >
            <MdDelete />
            Eliminar
          </Button>
        </HStack>
      </Flex>

      {/* Loading */}
      {isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {/* Not found */}
      {!metaQ.isLoading && !categoria && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="8"
        >
          <Text color="dashboard.fg.default" fontWeight="bold">
            Categoría no encontrada
          </Text>
          <Text color="dashboard.fg.muted" fontSize="sm" mt="1">
            Revisa que el periodo seleccionado sea el correcto.
          </Text>
          <Button
            variant="outline"
            color="dark"
            mt="4"
            radius="xl"
            onClick={() => navigate("/dashboard/club/categorias")}
          >
            Ver todas las categorías
          </Button>
        </Box>
      )}

      {/* Tags */}
      {!isLoading && categoria && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="5"
          mb="4"
        >
          <Text fontWeight="700" color="dashboard.fg.default" mb="3">
            Tags
          </Text>
          {categoriaTags.length > 0 ? (
            <Flex gap="2" wrap="wrap">
              {categoriaTags.map((tag) => (
                <Button
                  key={tag.id}
                  variant="surface"
                  color="info"
                  radius="pill"
                  size="sm"
                  onClick={() => navigate(`/dashboard/club/tags/${tag.id}`)}
                >
                  <MdLocalOffer />
                  {tag.nombre}
                </Button>
              ))}
            </Flex>
          ) : (
            <Text fontSize="sm" color="dashboard.fg.muted">
              Esta categorÃ­a no tiene tags asignados.
            </Text>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && categoria && cards.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">
            No hay actividades en esta categoría.
          </Text>
        </Box>
      )}

      {/* Cards grid */}
      {!isLoading && cards.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} gap="4">
          {cards.map((item) => (
            <CardActs
              key={item.id}
              {...item}
              onDelete={onDelete}
              onTogglePublish={() => onTogglePublish(item.id, item.published)}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Edit dialog */}
      <FormDialog
        open={editDialog.open}
        onOpenChange={editDialog.onOpenChange}
        title="Editar categoría"
        saving={patchCategoria.isPending}
        onCancel={editDialog.close}
        onSave={onEditSave}
        saveLabel="Guardar"
        errorText={
          patchCategoria.isError
            ? String(patchCategoria.error?.message || patchCategoria.error)
            : ""
        }
      >
        <Input
          placeholder="Nombre de la categoría"
          bg="dashboard.bg.input"
          borderColor="dashboard.border.default"
          {...editDialog.bind("nombre")}
          onKeyDown={(e) => e.key === "Enter" && onEditSave()}
        />
        <TagCombobox
          label="Tags"
          placeholder="Agregar tag..."
          items={tags.map((tag) => ({ label: tag.nombre, value: tag.id }))}
          value={editDialog.form.tagIds}
          onChange={(value) => editDialog.setField("tagIds", value)}
          onCreateItem={async (nombre) => {
            const created = await createTag.mutateAsync(nombre);
            return { label: created.nombre, value: created.id };
          }}
          createLabel="Crear tag"
        />
      </FormDialog>
    </Box>
  );
};

export default CategoriaDetail;
